/**
 * FAQList.tsx
 *
 * File purpose:
 * - Reusable visible FAQ list component used to render FAQ items on landing and other pages.
 * - Ensures FAQ content is aligned to the left edge of the page (viewport), not the centered container.
 * - This variant reduces vertical height and lowers font sizes for a more compact FAQ block.
 */

import React from 'react'

/**
 * FAQItemUI
 * - Represents a single visible FAQ item.
 */
export interface FAQItemUI {
  /** Question text */
  question: string
  /** Answer text (plain text or simple HTML-safe string) */
  answer: string
}

/**
 * Props for FAQList component
 */
export interface FAQListProps {
  /** Array of FAQ items to render */
  items: FAQItemUI[]
  /** Optional className for outer container */
  className?: string
}

/**
 * FAQList
 *
 * Purpose:
 * - Render an accessible list of FAQs using details/summary.
 * - Align the FAQ block to the left side of the page (viewport) while keeping max width for text readability.
 * - Compact styling: reduced paddings, smaller heading and body font sizes to decrease overall block height.
 *
 * @param props FAQListProps
 * @returns JSX.Element | null
 */
export default function FAQList({ items, className = '' }: FAQListProps): JSX.Element | null {
  if (!items || items.length === 0) return null

  return (
    <section
      /* Full-width section with page-level left padding so content starts at the page gutter */
      className={`w-full mt-6 px-4 sm:px-6 lg:px-8 ${className}`}
      aria-labelledby="faq-heading"
    >
      {/* Constrain text width but do NOT center; keep left alignment to the page gutter */}
      <div className="max-w-4xl">
        <h2 id="faq-heading" className="text-xl font-semibold mb-3">
          Frequently Asked Questions
        </h2>

        <div className="space-y-2">
          {items.map((it, idx) => (
            <details
              key={idx}
              className="group bg-white border border-gray-200 rounded-lg p-3 shadow-sm [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex items-center justify-between cursor-pointer list-none text-gray-800 font-medium text-sm">
                <span className="text-left">{it.question}</span>
                <span className="ml-3 text-orange-500 text-sm transform group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <div className="mt-2 text-gray-600 prose max-w-none text-left text-sm">
                <p>{it.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}